"use client";

import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";

interface BenefitItem {
  icon: string;
  title: string;
  description: string;
}

const benefits: BenefitItem[] = [
  {
    icon: "🎯",
    title: "Targeted Project Leads",
    description:
      "Homeowners and designers actively searching for plaster, microcement, and Venetian finishes – not generic contractor traffic.",
  },
  {
    icon: "📍",
    title: "Local Search Visibility",
    description:
      "Rank for searches like 'microcement applicator [city]' and 'Venetian plaster installer near me' so clients find you first.",
  },
  {
    icon: "💰",
    title: "Faster ROI",
    description:
      "Most applicators see qualified inquiries within days. One premium wall or bath project can cover your annual listing.",
  },
  {
    icon: "🏆",
    title: "Specialist Positioning",
    description:
      "Differentiate from generic painters and remodelers. Showcase artisan craft, curated finishes, and elevated project outcomes.",
  },
  {
    icon: "📱",
    title: "Mobile-First Experience",
    description:
      "Clients research surface finishes on phones. Your profile loads fast, looks refined, and converts intent into inquiries.",
  },
  {
    icon: "🔄",
    title: "Effortless Profile Control",
    description:
      "Update services, portfolio shots, service radius, and hours anytime through a clean, non-technical dashboard UI.",
  },
];

export function WhyChooseUsSection() {
  return (
    <section className="py-20 md:py-32 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-4xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-foreground">
            Why Applicators Choose <span className="text-primary">Us</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            America&apos;s only dedicated plaster &amp; microcement applicator
            directory – built to elevate artisan finishes and connect you with
            serious project inquiries.
          </p>
        </motion.div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
          {benefits.map((item, idx) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
            >
              <Card className="h-full p-8 flex flex-col items-center text-center bg-muted/40 dark:bg-muted border border-border/60">
                <div className="text-4xl mb-4" aria-hidden="true">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-foreground">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
